import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import { AbstractTranslatableAlertService, CoreState, CustomerManagementSelector } from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { CookieService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { addDays } from "date-fns";
import { EMPTY } from "rxjs";
import { tap } from "rxjs/operators";

import { PushMessageDialogComponent } from "./push-message-dialog/push-message-dialog.component";
import { PushMessageConfigService } from "./push-message.config";
import { PUSH_MESSAGE } from "./push-message.const";

declare function xtremepush(...args: any): any;

@Injectable({ providedIn: "root" })
export class PushMessageService implements OnDestroy {
	private setUserId$$ = this.setUserId$().subscribe();

	constructor(
		private alertService: AbstractTranslatableAlertService,
		private configService: PushMessageConfigService,
		private cookieService: CookieService,
		private store: Store<CoreState>
	) {}

	initialize(route: string) {
		const isDisabledRoute = this.configService.getDisabledRoutes().includes(route);
		if (typeof xtremepush === "undefined" || isDisabledRoute) {
			return;
		}

		const cookieValue = this.cookieService.getJson(PUSH_MESSAGE.COOKIE_NAME);
		if (!cookieValue) {
			xtremepush("ready", () => {
				if (xtremepush("push", "permission") !== "default") {
					return;
				}

				this.setCookie(this.configService.getCloseDays(), this.configService.getCloseTime());
				this.alertService.open(
					{
						id: PUSH_MESSAGE.DIALOG_ID,
						testId: PUSH_MESSAGE.DIALOG_ID,
						messageComponent: PushMessageDialogComponent,
						messageComponentInit: (component: PushMessageDialogComponent) =>
							(component.data = {
								title: PUSH_MESSAGE.TRANSLATION.TITLE,
								message: PUSH_MESSAGE.TRANSLATION.TEXT,
								buttons: [
									{
										color: AlertButtonColor.Primary,
										text: PUSH_MESSAGE.TRANSLATION.BLOCK,
										testIdSuffix: PUSH_MESSAGE.BLOCK,
										handler: () => {
											this.setCookie(this.configService.getBlockDays());
										},
									},
									{
										color: AlertButtonColor.Accent,
										text: PUSH_MESSAGE.TRANSLATION.ALLOW,
										testIdSuffix: PUSH_MESSAGE.ALLOW,
										handler: () => {
											xtremepush("prompt");
											this.cookieService.delete(PUSH_MESSAGE.COOKIE_NAME);
										},
									},
								],
							}),
					},
					[TranslationGroup.Common]
				);
			});
		} else if (cookieValue === PUSH_MESSAGE.COOKIE_VALUE.OPEN_POPUP_NEXT_TIME) {
			this.cookieService.delete(PUSH_MESSAGE.COOKIE_NAME);
		}
	}

	ngOnDestroy() {
		this.setUserId$$.unsubscribe();
	}

	private setUserId$() {
		if (typeof xtremepush !== "undefined") {
			return this.store.select(CustomerManagementSelector.getCustomer).pipe(
				ignoreNil(),
				tap((customer) => xtremepush("set", "user_id", customer?.customerId))
			);
		}

		return EMPTY;
	}

	private setCookie(days: number, time = "") {
		const expiryDate = addDays(new Date(), days);

		if (time) {
			const closeTime = time.split(":");
			expiryDate.setHours(Number(closeTime[0]));
			expiryDate.setMinutes(Number(closeTime[1]));
			expiryDate.setSeconds(Number(closeTime[2]));
		}

		this.cookieService.set({
			name: PUSH_MESSAGE.COOKIE_NAME,
			value: PUSH_MESSAGE.COOKIE_VALUE.DO_NOT_OPEN_POPUP,
			secure: true,
			expiryDate,
		});
	}
}
